import { useSelector } from 'react-redux';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { fetchPreapprovalVerdict } from '@/apiCalls/fetchPreapprovalVerdict';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    EBorrowerCitizenshipStatus,
    ELoanUsage,
    selectAddressByIndex,
    selectEmail,
    selectFirstName,
    selectLastName,
    selectLicenseNumber,
    selectLicenseState,
    selectLoanUsage,
    selectResidencyStatus,
    selectSellerSelectedStoreId,
    selectVisaNumber,
    setIsPreapproved,
} from '@/context/borrower';
import {
    EDisplays,
    EJourneys,
    EModals,
    selectInitialJourney,
    selectIsNZ,
    selectPartner,
    setDisplay,
    setModal,
} from '@/context/display';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { isBirthDateValid } from '@/utils/checkBirthDate';
import { isLicenseExpiryDateValid } from '@/utils/checkLicenseExpiryDate';
import { isVisaExpiryDateValid } from '@/utils/checkVisaExpiryDate';
import { EPreapprovalLogic } from '@/utils/EPreapprovalLogic';
import { isEmail } from '@/utils/isEmail';

interface IOcrSummaryButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId?: string;
}

export function OcrSummaryButtons({
    setShowErrors,
    baseId,
}: IOcrSummaryButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    // Contact Details
    const firstName = useAppSelector(selectFirstName);
    const lastName = useAppSelector(selectLastName);
    const email = useAppSelector(selectEmail);
    // Personal Information
    // const gender = useAppSelector(selectGender);
    const licenseNumber = useAppSelector(selectLicenseNumber);
    const licenseState = useAppSelector(selectLicenseState);
    const residencyStatus = useAppSelector(selectResidencyStatus);
    const visaNumber = useAppSelector(selectVisaNumber);
    // Address Information
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { ownershipType, streetNumber, streetName, suburb, state, postcode } =
        address;
    const storeId = useAppSelector(selectSellerSelectedStoreId);
    const partner = useAppSelector(selectPartner);
    const { storeSelectorOptions } = partner;

    const initialJourney = useAppSelector(selectInitialJourney);
    const isTestDrive = initialJourney === EJourneys.TEST_DRIVE;
    const isNZ = useAppSelector(selectIsNZ);
    const loanUsage = useAppSelector(selectLoanUsage);

    const preapprovalLogic = partner.preapprovalLogic;

    function isComplete(): boolean {
        // Contact Details
        if (firstName === '' || firstName === undefined) return false;
        if (lastName === '' || lastName === undefined) return false;
        if (!isEmail(email)) return false;

        if (
            residencyStatus === EBorrowerCitizenshipStatus.VISA_HOLDER &&
            !isTestDrive &&
            !isNZ &&
            (visaNumber === undefined ||
                visaNumber === 0 ||
                !isVisaExpiryDateValid())
        )
            return false;
        // Personal Information
        // if (gender === null || gender === undefined) return false;
        if (!isBirthDateValid()) return false;

        // For NZ, license fields are optional; for AU, they are required
        if (isNZ) {
            // For NZ, license fields are optional - no validation needed
        } else {
            if (licenseNumber === '' || licenseNumber === undefined)
                return false;
            if (licenseState === '' || licenseState === undefined) return false;
            if (!isLicenseExpiryDateValid()) return false;
        }

        if (
            !isTestDrive &&
            (residencyStatus === null || residencyStatus === undefined)
        )
            return false;

        // Address Information
        if (
            !isTestDrive &&
            (ownershipType === null || ownershipType === undefined)
        )
            return false;
        if (streetNumber === '' || streetNumber === undefined) return false;
        if (streetName === '' || streetName === undefined) return false;
        if (suburb === '' || suburb === undefined) return false;
        if (state === null || state === undefined) return false;
        if (
            postcode === '' ||
            postcode === null ||
            postcode === undefined ||
            postcode.toString().length < 4
        ) {
            return false;
        }

        return true;
    }
    function handleBackClick(): void {
        if (
            isTestDrive &&
            store.getState().borrowerReducer.personalDetails.licenseNumber !==
                '' &&
            store.getState().borrowerReducer.personalDetails.licenseNumber !==
                undefined
        ) {
            dispatch(setDisplay(EDisplays.TEST_DRIVE_LANDING_PAGE));
            return;
        }
        dispatch(setDisplay(EDisplays.IDENTIFICATION));
    }

    function handleNextClick(): void {
        setShowErrors(true);

        if (!isComplete()) {
            dispatch(setModal(EModals.OCR_CHECK_INCOMPLETE));
            return;
        }
        // Test Drive
        if (isTestDrive) {
            dispatch(setDisplay(EDisplays.TEST_DRIVE_AGREEMENT));
            createOrUpdateBorrower();
            return;
        }

        // Check for store id if store options exist
        if (storeSelectorOptions && !storeId) {
            dispatch(setDisplay(EDisplays.STORE_SELECTOR));
            return;
        }
        if (loanUsage === ELoanUsage.NOVATED) {
            if (preapprovalLogic === EPreapprovalLogic.SKIP_PREAPPROVAL) {
                dispatch(setDisplay(EDisplays.ASSETS_AND_LIABILITIES));
                createOrUpdateBorrower();
                return;
            }
            fetchPreapprovalVerdict().then((response) => {
                if (response.approvedLenders?.length) {
                    dispatch(setIsPreapproved(true));
                    dispatch(setDisplay(EDisplays.YOUR_SCORES));
                    createOrUpdateBorrower();
                    return;
                } else {
                    dispatch(setDisplay(EDisplays.INDICATIVELY_REFERRED));
                    createOrUpdateBorrower();
                    return;
                }
            });
        } else {
            // Normal
            if (preapprovalLogic === EPreapprovalLogic.SKIP_PREAPPROVAL) {
                // For business loans, skip Employment and go to Assets & Liabilities; for personal loans, go to Employment
                if (loanUsage === ELoanUsage.BUSINESS) {
                    dispatch(setDisplay(EDisplays.ASSETS_AND_LIABILITIES));
                } else {
                    dispatch(setDisplay(EDisplays.EMPLOYMENT));
                }
                createOrUpdateBorrower();
                return;
            }
            dispatch(setDisplay(EDisplays.PREPPROVAL_ANIMATION));
            createOrUpdateBorrower();
        }
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
